<template>
<!-- fullstory: fs-mask keeps registrant contact pii out of recordings -->
  <div class="fr-page">
    <div class="fr-card fs-mask">
      <div class="fr-head">
        <div class="fr-logo">
          <svg viewBox="-2 0 200 128" fill="none" role="img" aria-label="Fleet Integrations">
            <g stroke="#1976D2" stroke-width="8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M36 120 C18 120 4 106 4 88 C4 74 14 62 28 57 C26 42 36 30 52 30 C58 30 64 32 68 36 C76 18 92 8 110 8 C138 8 160 30 162 57 C180 60 192 76 192 92 C192 108 178 120 162 120 Z"/>
              <path d="M65 64 H133 M116 48 L133 64 L116 80"/>
              <path d="M133 92 H65 M82 76 L65 92 L82 108"/>
            </g>
          </svg>
        </div>
        <h1 class="fr-title">Register your company</h1>
        <p class="fr-subtitle">Tell us about your operation and we'll get you set up.</p>
      </div>

      <div class="fr-note">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>
        <span>No payment to start. Share a few details and our team will reach out to finish your setup.</span>
      </div>

      <form @submit.prevent="register">
        <section class="fr-section">
          <h2 class="fr-sec">Company</h2>
          <div class="fr-grid">
            <div class="fr-field">
              <label class="fr-label" for="company_name">Company name</label>
              <div class="fr-control"><input id="company_name" v-model="form.company_name" type="text" required placeholder="Acme Coach Lines" /></div>
            </div>
            <div class="fr-field">
              <label class="fr-label" for="timezone">Time zone</label>
              <div class="fr-control fr-select">
                <select id="timezone" v-model="form.timezone">
                  <option :value="undefined" disabled selected>Select a time zone</option>
                  <option v-for="o in timezone_options" :key="o.value" :value="o.value">{{ o.text }}</option>
                </select>
                <svg class="fr-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
              </div>
            </div>
          </div>
        </section>

        <section class="fr-section">
          <h2 class="fr-sec">Primary contact</h2>
          <div class="fr-field">
            <label class="fr-label" for="contact_name">Full name</label>
            <div class="fr-control"><input id="contact_name" v-model="form.contact_name" type="text" required placeholder="Jane Operator" /></div>
          </div>
          <div class="fr-grid">
            <div class="fr-field">
              <label class="fr-label" for="email">Email address</label>
              <div class="fr-control"><input id="email" v-model="form.username" type="email" required placeholder="jane@acmecoach.com" /></div>
            </div>
            <div class="fr-field">
              <label class="fr-label" for="phone">Phone number</label>
              <div class="fr-control"><input id="phone" v-model="form.phone" type="tel" placeholder="(555) 555-0123" /></div>
            </div>
          </div>
        </section>

        <section class="fr-section">
          <h2 class="fr-sec">Your fleet</h2>
          <div class="fr-field">
            <label class="fr-label" for="vehicle_count">How many vehicles do you run?</label>
            <div class="fr-control fr-select">
              <select id="vehicle_count" v-model="form.vehicle_count">
                <option :value="undefined" disabled selected>Select a range</option>
                <option v-for="o in vehicle_count_options" :key="o.value" :value="o.value">{{ o.text }}</option>
              </select>
              <svg class="fr-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </div>

          <div class="fr-field">
            <span class="fr-label">Which features are you interested in?</span>
            <div class="fr-chips">
              <label class="fr-chip" v-for="o in feature_options" :key="o.value" :class="{ on: form.features.includes(o.value) }">
                <input type="checkbox" :value="o.value" v-model="form.features" />
                <span>{{ o.text }}</span>
              </label>
            </div>
          </div>

          <div class="fr-field">
            <label class="fr-label" for="booking_manager">Integrate your booking platform?</label>
            <div class="fr-control fr-select">
              <select id="booking_manager" v-model="form.booking_manager">
                <option v-for="o in booking_manager_options" :key="o.value" :value="o.value">{{ o.text }}</option>
              </select>
              <svg class="fr-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </div>
          <div class="fr-field">
            <label class="fr-label" for="telematics_type">Integrate your telematics platform?</label>
            <div class="fr-control fr-select">
              <select id="telematics_type" v-model="form.telematics_type">
                <option v-for="o in telematics_options" :key="o.value" :value="o.value">{{ o.text }}</option>
              </select>
              <svg class="fr-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </div>

          <div v-if="form.telematics_type == '' && form.features.includes('bus_sos')" class="fr-yard">
            <p class="fr-yard-title">Add a vehicle yard <span>optional, can be managed later</span></p>
            <div class="fr-field">
              <label class="fr-label" for="yard_name">Yard name</label>
              <div class="fr-control"><input id="yard_name" v-model="form.yard_name" type="text" placeholder="Main yard" /></div>
            </div>
            <div class="fr-field">
              <label class="fr-label" for="yard_address">Yard full address</label>
              <div class="fr-control"><textarea id="yard_address" v-model="form.yard_address" rows="2" placeholder="123 Depot Rd, City, ST 00000"></textarea></div>
            </div>
            <p class="fr-yard-alt"><router-link to="/register/sos">Yard-only? Register for Bus SOS in minutes &rarr;</router-link></p>
          </div>
        </section>

        <section class="fr-section">
          <h2 class="fr-sec">Anything else?</h2>
          <div class="fr-field">
            <label class="fr-label" for="comments">Questions or comments</label>
            <div class="fr-control"><textarea id="comments" v-model="form.comments" rows="3" placeholder="Tell us anything that would help us prepare."></textarea></div>
          </div>
        </section>

        <!-- honeypot: off-screen, bots fill it, the api quietly drops the submission -->
        <div class="fr-hp" aria-hidden="true">
          <label for="website_url">Website</label>
          <input id="website_url" v-model="form._honeypot" type="text" tabindex="-1" autocomplete="off" />
        </div>

        <transition name="fr-fade">
          <div v-if="errored" class="fr-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>
            <span>Something went wrong submitting your registration. Please try again or contact support.</span>
          </div>
        </transition>

        <button id="login-button" class="fr-submit" type="submit" :disabled="submitting">
          <span v-if="submitting" class="fr-spinner" aria-hidden="true"></span>
          <span>{{ submitting ? 'Submitting&hellip;' : 'Submit registration' }}</span>
        </button>
        <p class="fr-reassure">Our team will contact you to complete your setup. No payment required to start.</p>
      </form>

      <div class="fr-foot">
        <div class="fr-foot-links">
          <router-link class="fr-link" :to="{ path: '/login' }">Log in</router-link>
          <span class="fr-dot">&bull;</span>
          <a class="fr-foot-a" target="_new" href="https://www.fleet-integrations.com">Learn more</a>
          <span class="fr-dot">&bull;</span>
          <a class="fr-foot-a" href="mailto:support@initekconsulting.com">Contact us</a>
        </div>
        <a class="fr-powered" target="_new" href="https://www.initekconsulting.com/fleet-integrations">
          <span>powered by</span><img height="18" src="/initek-logo.png" alt="Initek" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>

import {register_tenant} from "../networking/apis";

export default {
  name: 'direct_register',
  components: {
  },

  data() {
    return {
      submitting: false,
      errored: false,
      form: { features: [], booking_manager: '', telematics_type: '', _honeypot: '' },
      feature_options: [
        { text: 'Actuals Integration', value: 'booking_actuals' },
        { text: 'Views & Reports', value: 'booking_views' },
        { text: 'Alerts & Notifications', value: 'booking_alerts' },
        { text: 'Vehicle Status Management', value: 'veh_status_mgmt' },
        { text: 'Data Integrations', value: 'data_services' },
        { text: 'Bus SOS', value: 'bus_sos' },
      ],
      booking_manager_options: [
        { text: 'Not At This Time', value: '' },
        { text: 'Yes, With Distinctive Coach Manager', value: 'CM' },
        { text: 'Yes, With TBN OpsDriver', value: 'TBN' },
      ],
      telematics_options: [
        { text: 'Not At This Time', value: '' },
        { text: 'Yes, With Samsara', value: 'SAMSARA' },
        { text: 'Yes, With Saucon', value: 'SAUCON' },
        { text: 'Yes, With Motive', value: 'MOTIVE' },
        { text: 'Yes, With Zonar', value: 'ZONRA' },
        { text: 'Yes, With Another Provider', value: 'OTHER' },
      ],
      vehicle_count_options: [
        { text: 'Less Than 25', value: '25' },
        { text: '25 - 50', value: '25+' },
        { text: '50 - 100', value: '50+' },
        { text: '100+', value: '100+' },
      ],
      vehicle_type_options: [
        { text: 'Full Size', value: 'full' },
        { text: 'Mini', value: 'mini' },
        { text: 'Sprinter', value: 'sprinter' },
        { text: 'SUV', value: 'suv' },
        { text: 'Other', value: 'other' },
      ],
      timezone_options: [
        { text: 'US/Pacific', value: 'US/Pacific' },
        { text: 'US/Mountain', value: 'US/Mountain' },
        { text: 'US/Central', value: 'US/Central' },
        { text: 'US/Eastern', value: 'US/Eastern' },
        { text: 'America/Phoenix', value: 'America/Phoenix' },
        { text: 'America/New_York', value: 'America/New_York' },
        { text: 'America/Chicago', value: 'America/Chicago' },
        { text: 'Other', value: '' },
      ]
    }
  },
  async created() {
    localStorage.clear()
  },
  methods: {
    async register() {
      this.errored = false
      this.submitting = true
      try {
        await register_tenant(this.form)
        window.location.href = 'https://www.fleet-integrations.com';
      } catch (e) {
        this.errored = true
        this.submitting = false
      }
    },
  }
}
</script>

<style scoped>
/* register, sharing the login's fresh design language */
.fr-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 44px 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #0a0d14;
  background:
    radial-gradient(900px 500px at 50% -8%, rgba(25,118,210,0.12), transparent 60%),
    radial-gradient(700px 500px at 100% 110%, rgba(25,118,210,0.07), transparent 55%),
    linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%);
}
.fr-card {
  width: 100%;
  max-width: 600px;
  background: #fff;
  border: 1px solid rgba(16,24,40,0.06);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(16,24,40,0.10), 0 2px 6px rgba(16,24,40,0.04);
  padding: 38px 40px 30px;
  box-sizing: border-box;
  text-align: left;
}
.fr-head { text-align: center; margin-bottom: 20px; }
.fr-logo { width: 58px; margin: 0 auto 14px; }
.fr-logo svg { width: 100%; height: auto; display: block; }
.fr-title { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.01em; color: #1976D2; }
.fr-subtitle { margin: 5px 0 0; font-size: 15px; color: #5b6470; }

.fr-note {
  display: flex; align-items: center; gap: 10px;
  background: var(--blue-soft, rgba(25,118,210,0.08));
  border: 1px solid rgba(25,118,210,0.16);
  color: #1f4e7a;
  border-radius: 11px;
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.4;
  margin-bottom: 26px;
}
.fr-note svg { width: 19px; height: 19px; flex: none; color: #1976D2; }

.fr-section { margin-bottom: 24px; }
.fr-sec {
  font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
  color: #1976D2; margin: 0 0 14px; padding-bottom: 9px; border-bottom: 1px solid #eef1f5;
}
.fr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.fr-field { display: flex; flex-direction: column; gap: 7px; margin-bottom: 14px; }
.fr-grid .fr-field { margin-bottom: 0; }
.fr-field:last-child { margin-bottom: 0; }
.fr-label { font-size: 13px; font-weight: 600; color: #3a4250; }

.fr-control {
  display: flex; align-items: center; position: relative;
  background: #f5f7fa; border: 1.5px solid #e3e8ef; border-radius: 11px; padding: 0 14px;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.fr-control:focus-within { background: #fff; border-color: #1976D2; box-shadow: 0 0 0 4px rgba(25,118,210,0.12); }
.fr-control input, .fr-control select, .fr-control textarea {
  flex: 1 1 auto; min-width: 0; border: 0; outline: 0; background: transparent;
  /* 16px minimum: ios safari auto-zooms focused inputs below 16px */
  font-size: 16px; color: #0a0d14; font-family: inherit; padding: 12px 0;
}
.fr-control textarea { resize: vertical; line-height: 1.45; }
.fr-control input::placeholder, .fr-control textarea::placeholder { color: #a4abb8; }
.fr-select select { appearance: none; -webkit-appearance: none; padding-right: 22px; cursor: pointer; }
.fr-chev { position: absolute; right: 14px; width: 17px; height: 17px; color: #98a2b3; pointer-events: none; }

.fr-chips { display: flex; flex-wrap: wrap; gap: 9px; }
.fr-chip {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 14px; border-radius: 999px; cursor: pointer; user-select: none;
  background: #f5f7fa; border: 1.5px solid #e3e8ef;
  font-size: 13.5px; font-weight: 600; color: #3a4250;
  transition: all .14s ease;
}
.fr-chip input { position: absolute; opacity: 0; width: 0; height: 0; }
.fr-chip span { position: relative; }
.fr-chip span::before {
  content: ''; display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  background: #c2c9d4; margin-right: 8px; vertical-align: middle; transition: background .14s ease;
}
.fr-chip:hover { border-color: #c8d2e0; }
.fr-chip.on { background: var(--blue-soft, rgba(25,118,210,0.10)); border-color: #1976D2; color: #1565C0; }
.fr-chip.on span::before { background: #1976D2; }

.fr-yard {
  margin-top: 14px; padding: 16px; border-radius: 12px;
  background: #f8fafc; border: 1px dashed #cdd7e3;
}
.fr-yard-title { margin: 0 0 12px; font-size: 13.5px; font-weight: 650; color: #3a4250; }
.fr-yard-title span { font-weight: 500; color: #5b6470; }
.fr-yard-alt { margin: 12px 0 0; font-size: 13px; }
.fr-yard-alt a { color: #1976D2; font-weight: 600; text-decoration: none; }
.fr-yard-alt a:hover { color: #1565C0; text-decoration: underline; }

.fr-hp { position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden; }

.fr-error {
  display: flex; align-items: center; gap: 9px; margin-bottom: 14px;
  padding: 11px 13px; background: #fdecea; color: #b5372a; border-radius: 10px; font-size: 13.5px; line-height: 1.35;
}
.fr-error svg { width: 18px; height: 18px; flex: none; }

.fr-submit {
  width: 100%; height: 50px; margin-top: 6px; border: 0; border-radius: 11px;
  background: linear-gradient(180deg, #1f86e6 0%, #1565C0 100%);
  color: #fff; font-size: 15.5px; font-weight: 650; letter-spacing: 0.01em; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  box-shadow: 0 8px 18px rgba(25,118,210,0.28);
  transition: filter .15s ease, transform .04s ease, box-shadow .15s ease;
}
.fr-submit:hover:not(:disabled) { filter: brightness(1.05); }
.fr-submit:active:not(:disabled) { transform: translateY(1px); }
.fr-submit:disabled { opacity: 0.65; cursor: default; box-shadow: none; }
.fr-spinner { width: 17px; height: 17px; border: 2.5px solid rgba(255,255,255,0.45); border-top-color: #fff; border-radius: 50%; animation: fr-spin 0.7s linear infinite; }
@keyframes fr-spin { to { transform: rotate(360deg); } }

.fr-reassure { margin: 13px 0 0; text-align: center; font-size: 13px; color: #5b6470; }

.fr-foot {
  margin-top: 24px; padding-top: 18px; border-top: 1px solid #eef1f5;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.fr-foot-links { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: center; }
.fr-link, .fr-foot-a { color: #5b6470; font-size: 13px; font-weight: 500; text-decoration: none; }
.fr-link { color: #1976D2; font-weight: 600; }
.fr-foot-a:hover, .fr-link:hover { color: #1565C0; }
.fr-dot { color: #c2c9d4; font-size: 12px; }
.fr-powered { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: #98a2b3; text-decoration: none; }
.fr-powered img { opacity: 0.9; }

.fr-fade-enter-active, .fr-fade-leave-active { transition: opacity .2s ease; }
.fr-fade-enter, .fr-fade-leave-to { opacity: 0; }

@media (max-width: 560px) {
  .fr-card { padding: 30px 22px 24px; }
  .fr-grid { grid-template-columns: 1fr; gap: 0; }
  .fr-grid .fr-field { margin-bottom: 14px; }
}
</style>
